<template>
  <AuthLayout :mode="mode">
    <Section heading="Manage Contacts" :mode="mode" />
    <AppCard :mode="mode">
      <template #body>
        <div class="mb-6">
          <div
            class="grid grid-cols-5 gap-x-3 gap-y-4 md:grid-cols-3 sm:grid-cols-1"
          >
            <AppInput
              :mode="mode"
              id="company"
              title="Company"
              placeholder="Company"
              v-model="contactSearch.company_name"
            />
            <AppInput
              :mode="mode"
              id="first-name"
              title="First name"
              placeholder="First name"
              v-model="contactSearch.first_name"
            />
            <AppInput
              :mode="mode"
              id="last-name"
              title="Last name"
              placeholder="Last name"
              v-model="contactSearch.last_name"
            />
            <AppInput
              :mode="mode"
              id="email"
              title="Email"
              placeholder="Email"
              v-model="contactSearch.email"
            />
            <AppSelect
              :mode="mode"
              title="Country"
              v-model="contactSearch.country"
            >
              <template #options>
                <option value="" selected></option>
                <option
                  v-for="(country, key) in countries"
                  :value="country.code"
                  :key="key"
                >
                  {{ country.country }}
                </option>
              </template>
            </AppSelect>
            <AppInput
              :mode="mode"
              id="username"
              title="Username"
              placeholder="Username"
              v-model="contactSearch.username"
            />
          </div>
          <div class="mt-6 grid gap-2 sm:mt-4">
            <AppButton title="Search" @action="search" />
          </div>
        </div>
        <template v-if="paginatedItems.length > 0">
          <AppTable :mode="mode" :columns="columns">
            <template #rows>
              <tr v-for="(contact, key) in paginatedItems" :key="key">
                <td>
                  <FallbackName
                    :to="{
                      name: 'contact-details',
                      params: {
                        id: contact._source.contact.contact_id,
                      },
                    }"
                    :first="contact._source.contact.first_name"
                    :last="contact._source.contact.last_name"
                  />
                </td>
                <td>
                  <FallbackText :value="contact._source.contact.email" />
                </td>
                <td>
                  <FallbackText :value="contact._source.member.company_name" />
                </td>
                <td>
                  <FallbackText :value="contact._source.contact.username" />
                </td>
                <td>
                  <FallbackText :value="contact._source.contact.title" />
                </td>
                <td>
                  <div class="flex items-center gap-2">
                    <TableAction
                      :mode="mode"
                      @itemAction="
                        $redirectTo(
                          'contact-details',
                          contact._source.contact.contact_id,
                        )
                      "
                      action="view"
                    />
                    <TableAction
                      :mode="mode"
                      @itemAction="
                        $redirectTo(
                          'contact-edit',
                          contact._source.contact.contact_id,
                        )
                      "
                      action="edit"
                    />
                    <TableAction
                      :mode="mode"
                      :isActive="
                        contact._source.contact.contact_id !==
                        loggedInUser.contact_id
                      "
                      @itemAction="
                        confirmDelete(contact._source.contact.contact_id)
                      "
                      action="delete"
                    />
                  </div>
                </td>
              </tr>
            </template>
          </AppTable>
          <MobileTable
            :mode="mode"
            :fields="{
              fullname: 'Full Name',
              email: 'Email',
              company: 'Company',
              title: 'Title',
              username: 'Username',
              actions: 'Actions',
            }"
            :data="paginatedItems"
          >
            <template #default="{ row, fieldKey }">
              <template v-if="fieldKey === 'fullname'">
                <FallbackName
                  :to="{
                    name: 'contact-details',
                    params: {
                      id: row._source.contact.contact_id,
                    },
                  }"
                  :first="row._source.contact.first_name"
                  :last="row._source.contact.last_name"
                  :isTruncate="false"
                />
              </template>
              <template v-else-if="fieldKey === 'email'">
                <FallbackText
                  :value="row._source.contact.email"
                  :isTruncate="false"
                />
              </template>
              <template v-else-if="fieldKey === 'company'">
                <FallbackText
                  :value="row._source.member.company_name"
                  :isTruncate="false"
                />
              </template>
              <template v-else-if="fieldKey === 'title'">
                <FallbackText
                  :value="row._source.contact.title"
                  :isTruncate="false"
                />
              </template>
              <template v-else-if="fieldKey === 'username'">
                <FallbackText
                  :value="row._source.contact.username"
                  :isTruncate="false"
                />
              </template>
              <template v-else-if="fieldKey === 'actions'">
                <div class="flex items-center gap-2">
                  <TableAction
                    :mode="mode"
                    @itemAction="
                      $redirectTo(
                        'contact-details',
                        row._source.contact.contact_id,
                      )
                    "
                    action="view"
                  />
                  <TableAction
                    :mode="mode"
                    @itemAction="
                      $redirectTo(
                        'contact-edit',
                        row._source.contact.contact_id,
                      )
                    "
                    action="edit"
                  />
                  <TableAction
                    :mode="mode"
                    :isActive="
                      row._source.contact.contact_id !== loggedInUser.contact_id
                    "
                    @itemAction="confirmDelete(row._source.contact.contact_id)"
                    action="delete"
                  />
                </div>
              </template>
            </template>
          </MobileTable>
        </template>
        <EmptyData
          v-else
          :notFound="'No contacts represented!'"
          :isClear="true"
          @clear="clear"
          :mode="mode"
        />
        <AppPagination
          :mode="mode"
          :data="contacts"
          @pageChanged="updatePaginatedItems"
        />
      </template>
    </AppCard>
    <DeleteRecord
      :currentId="currentContactId"
      :mode="mode"
      @recordDeleted="recordDeleted"
      @cancelDelete="currentContactId = null"
    />
  </AuthLayout>
</template>

<script>
import Section from "@C/Common/Section.vue";
import ApiRequest from "@L/ApiRequest";
import { useGlobalErrorHandler } from "@L/errorHandler";
import { useUserStore } from "@S/user";
import { useToastStore } from "@S/toast";
import { useLoaderStore } from "@S/loader";
import { useCountriesStore } from "@S/countries";
import EmptyData from "@C/Common/EmptyData.vue";
import DeleteRecord from "@C/DeleteRecord.vue";
import AppInput from "@C/Form/AppInput.vue";
import AppSelect from "@C/Form/AppSelect.vue";
import AppCard from "@C/Common/AppCard.vue";
import AppPagination from "@C/Common/AppPagination.vue";
import AppTable from "@C/Tables/AppTable.vue";
import AuthLayout from "@A/AuthLayout.vue";
import MobileTable from "@C/Tables/MobileTable.vue";
import FallbackText from "@C/Tables/FallbackText.vue";
import FallbackName from "@C/Tables/FallbackName.vue";
import AppButton from "@C/Common/AppButton.vue";
import TableAction from "@C/Tables/TableAction.vue";

export default {
  name: "ManageContacts",
  components: {
    AuthLayout,
    Section,
    EmptyData,
    DeleteRecord,
    AppInput,
    AppSelect,
    AppCard,
    AppPagination,
    AppTable,
    TableAction,
    MobileTable,
    FallbackText,
    FallbackName,
    AppButton,
  },
  setup() {
    const userStore = useUserStore();
    const toastStore = useToastStore();
    const loaderStore = useLoaderStore();
    const countriesStore = useCountriesStore();
    const { handleError } = useGlobalErrorHandler();
    return {
      userStore,
      toastStore,
      loaderStore,
      countriesStore,
      handleError,
    };
  },
  props: {
    mode: String,
  },
  data() {
    return {
      contacts: [],
      contactSearch: {
        first_name: "",
        last_name: "",
        email: "",
        company_name: "",
        country: "",
        username: "",
      },
      columns: [
        {
          name: "Full Name",
        },
        {
          name: "Email",
        },
        {
          name: "Company",
        },
        {
          name: "Username",
        },
        {
          name: "Title",
        },
        {
          name: "Actions",
        },
      ],
      currentContactId: null,
      paginatedItems: [],
    };
  },
  created() {
    if (!this.isAdmin) {
      this.toastStore.show(
        "You do not have permission to see this page",
        "error",
      );
      this.$router.push({ name: "home" });
      return;
    }
  },
  methods: {
    async search() {
      this.loaderStore.setIsLoading(true);
      const payload = this.contactSearch;
      if (!this.contactSearch.first_name) {
        delete this.contactSearch.first_name;
      }
      if (!this.contactSearch.last_name) {
        delete this.contactSearch.last_name;
      }
      if (!this.contactSearch.email) {
        delete this.contactSearch.email;
      }
      if (!this.contactSearch.company_name) {
        delete this.contactSearch.company_name;
      }
      if (!this.contactSearch.country) {
        delete this.contactSearch.country;
      }
      if (!this.contactSearch.username) {
        delete this.contactSearch.username;
      }
      this.currentPage = 1;
      try {
        const { data } = await ApiRequest().post(`/contact_search`, payload);
        if (data) {
          this.contacts = data;
        } else {
          this.contacts = [];
        }
      } catch (e) {
        this.handleError(e);
      } finally {
        this.loaderStore.setIsLoading(false);
      }
    },
    clear() {
      this.contactSearch = {
        first_name: "",
        last_name: "",
        email: "",
        company_name: "",
        country: "",
        username: "",
      };
    },
    confirmDelete(id) {
      this.currentContactId = id;
    },
    recordDeleted() {
      this.search();
      this.currentContactId = null;
      this.toastStore.show("Contact deleted successfully!", "success");
    },
    updatePaginatedItems(paginatedData) {
      this.paginatedItems = paginatedData;
    },
  },
  computed: {
    loggedInUser() {
      return this.userStore.user.contact;
    },
    countries() {
      return this.countriesStore.countries;
    },
    isAdmin() {
      return this.userStore.user.global_admin;
    },
  },
};
</script>
